import { deepOmitKeys } from 'lib/object/objectUtils'

/**
 * Redacts emails from a string. Used for error messages and any free text that may contain PII.
 * Regex is created per call to avoid stateful /g lastIndex bug when reused across invocations.
 *
 * Scope: Only emails are redacted in free text. Names, phone numbers, passport numbers etc. are
 * intentionally not redacted due to high false-positive rates (e.g. "User", "Johnson" in product
 * names). PII in structured data is removed via piiKeys + deepOmitKeys.
 */
export function sanitizeErrorMessage(message: string | undefined): string {
  if (message == null || typeof message !== 'string') return 'Unknown error'
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g
  return message.replace(emailRegex, '[REDACTED]')
}

/**
 * Shortest substrings to match PII keys in object payloads.
 * Case-sensitive matching preserves debug keys (packageName, orderNumber, offerTitle).
 */
export const piiKeys = [
  'name', // name; receiver_name, giver_name, middle_name, first_name, last_name, surname, fullname (avoids packageName, fileName)
  'mail', // email, employeeEmail, customer_email, vendorBookingEmail, bookingEmail
  'firstName', // firstName (camelCase; name does not match due to capital N)
  'lastName', // lastName (camelCase)
  'full', // fullName (camelCase; name does not match due to capital N)
  'given', // givenName, given_name, customer_given_name
  'phone', // phone, phoneNumber, phone_number, telephone, international_phone_number
  'passport', // passport_number, passportNumber
  'nationalId', // nationalIdNumber (camelCase)
  'national_id', // national_id_number (snake_case)
  'post_code', // post_code
  'postcode', // postcode
  'postal', // postalCode
  'dob', // dob
  'birth', // date_of_birth, birthDate
  'Birth', // dateOfBirth (camelCase)
  'gender', // gender
  'job', // jobTitle (avoids offerTitle, packageTitle, pageTitle)
  'signature', // signature
  'Signature', // customerSignature (camelCase)
  'address', // address, streetAddress, street_address, emailAddress
  'street', // street, streetAddress, street_address
]

/**
 * Recursively sanitizes string values in an object. Used for Error objects and nested data.
 * Uses a visited set to avoid stack overflow on circular references.
 */
function deepSanitizeStrings(val: any, visited: Set<object> = new Set()): any {
  if (val == null) return val
  if (typeof val === 'string') return sanitizeErrorMessage(val)
  if (val instanceof Error) return { message: sanitizeErrorMessage(val.message), name: val.name }
  if (Array.isArray(val)) return val.map((item) => deepSanitizeStrings(item, visited))
  if (typeof val === 'object') {
    if (visited.has(val)) return '[Circular]'
    visited.add(val)
    try {
      return Object.fromEntries(
        Object.entries(val).map(([k, v]) => [k, deepSanitizeStrings(v, visited)]),
      )
    } finally {
      visited.delete(val)
    }
  }
  return val
}

/** Keys to preserve for debugging (contain PII substrings like 'name' but are not PII) */
const preservedDebugKeys = [
  'hostname', // Server/domain hostname for request tracing
  'displayName', // UI label (e.g. package name), not user's real name
  'emailHash', // SHA-256 hash of email, not PII
]

/**
 * Sanitizes data for logging (Sentry, New Relic, etc.): removes PII keys and redacts emails in strings.
 * May throw if data contains non-cloneable values (functions, DOM nodes, etc.); callers should wrap in try/catch.
 */
export function sanitizeForLogging<T extends Record<string, any>>(data: T): T {
  if (data == null || typeof data !== 'object') return data
  const withoutPiiKeys = deepOmitKeys(data, piiKeys, preservedDebugKeys)
  return deepSanitizeStrings(withoutPiiKeys) as T
}
